import { useState, type ReactNode } from "react";
import { SharedAccess, UserModelRole } from "target_wasm";
import { ModelElementType } from "@/components/entities";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { ShareModel } from "@/components/canvas/detailsSide/modelDetails/ShareModel";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { copyModelId as copyModelIdTo } from "@/components/canvas/utils/copyTableId";
import {
  canManageModelAccess,
  canOpenModelAccess,
} from "@/components/canvas/utils/roles";
import {
  renameModelRequest,
  fetchModelTablesForDeletion,
} from "./ModelServices";
import { DeleteModelDialog } from "./deletion/DeleteModelDialog";
import { nameLength } from "@/components/main/utils/naming";
import { MAX_MODEL_NAME_CHARS } from "./modelName";
import {
  type TableDeletionAction,
  isScheduleDeletionAction,
} from "./deletion/DeleteModelDialogTypes";

export interface ModelActionPermissions {
  canDeleteModel: boolean;
  /// `Owner` = manager of the model's space, the move door's source half.
  canMoveModel: boolean;
  /** Opens Manage access, as the canvas asks it. */
  canOpenAccess: boolean;
  canManageAccess: boolean;
}

export interface ModelActions {
  permissions: ModelActionPermissions;
  /** Optimistic rename — applies immediately, rolls back with a toast on
   *  server failure. Returns true so editors can close right away. */
  commitName: (name: string) => boolean;
  /** True while the delete request is in flight (row shows a spinner). */
  isDeleting: boolean;
  copyModelId: () => void;
  openShare: () => void;
  /** Analyse dependent tables, then delete immediately or open the dialog. */
  requestDelete: () => Promise<void>;
  /** Delete / share dialogs — render once in the caller's tree. */
  dialogs: ReactNode;
}

/** Model actions (delete, share, copy ID, rename), their permissions and dialogs;
 *  the caller builds its own menu from them. Used by `ModelRow`. */
export function useModelActions(model: ModelElementType): ModelActions {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharedWithList, setSharedWithList] = useState<SharedAccess>();

  const patchModel = useMainStateStore((s) => s.patchModel);
  const trashModel = useMainStateStore((s) => s.trashModel);
  const canDeleteCatalogModels = useMainStateStore(
    (s) => s.canDeleteCatalogModels,
  );

  const modelName = model.name || "Unnamed Model";

  const commitName = (raw: string): boolean => {
    const name = raw.trim();
    if (!name || nameLength(name) > MAX_MODEL_NAME_CHARS) return false;
    const rollback = patchModel(model.model_id, { name });
    void renameModelRequest(model.model_id, name).then((ok) => {
      if (!ok) {
        rollback();
        showNotificationGlobal("Failed to rename model", "error");
      }
    });
    return true;
  };

  const handleDeleteModelWithActions = async (
    tableActions: Record<string, TableDeletionAction>,
  ): Promise<void> => {
    const tableIds = Object.entries(tableActions)
      .filter(([, action]) => isScheduleDeletionAction(action))
      .map(([tableId]) => tableId);
    setIsDeleting(true);
    try {
      const ok = await trashModel(model, tableIds);
      showNotificationGlobal(
        ok ? "Model scheduled for deletion" : "Failed to delete model",
        ok ? "success" : "error",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const requestDelete = async (): Promise<void> => {
    try {
      const analysis = await fetchModelTablesForDeletion(model.model_id);
      const totalTables = analysis?.tables_js?.length || 0;
      if (totalTables === 0) {
        await handleDeleteModelWithActions({});
      } else {
        setDeleteDialogOpen(true);
      }
    } catch {
      setDeleteDialogOpen(true);
    }
  };

  const copyModelId = (): void => copyModelIdTo(model.model_id);

  const openShare = (): void => setShareDialogOpen(true);

  const access = {
    can_see_shared_with_list:
      model.user_access?.includes("CanSeeSharedWithList") ?? false,
    can_manage_access: model.user_access?.includes("CanManageAccess") ?? false,
    is_catalog: model.is_catalog,
  };
  const scheduled = !!model.scheduled_deletion_date;
  const permissions: ModelActionPermissions = {
    // A catalog model has no owner: the publish role trashes it.
    canDeleteModel:
      (model.is_catalog
        ? canDeleteCatalogModels
        : model.relation === "Owner") && !model.scheduled_deletion_date,
    canMoveModel:
      model.relation === "Owner" &&
      !model.scheduled_deletion_date &&
      !model.is_catalog &&
      !!model.space_id,
    canOpenAccess: canOpenModelAccess(access, scheduled),
    canManageAccess: canManageModelAccess(access, scheduled),
  };

  const dialogs = (
    <>
      {deleteDialogOpen && (
        <DeleteModelDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          model={model}
          onDelete={handleDeleteModelWithActions}
        />
      )}
      {shareDialogOpen && (
        <ShareModel
          modelID={model.model_id}
          modelName={modelName}
          role={
            model.relation in UserModelRole
              ? UserModelRole[model.relation as keyof typeof UserModelRole]
              : undefined
          }
          sharedWith={sharedWithList}
          setIsShare={() => setShareDialogOpen(false)}
          canManageAccess={permissions.canManageAccess}
          setSharedWithList={setSharedWithList}
        />
      )}
    </>
  );

  return {
    permissions,
    commitName,
    isDeleting,
    copyModelId,
    openShare,
    requestDelete,
    dialogs,
  };
}
